/**
 * La marca del tenant, leída una sola vez por sesión de pestaña.
 *
 * La barra lateral se vuelve a montar en cada navegación. Sin esta caché,
 * cambiar de pantalla pediría el logo al servidor otra vez y el nombre
 * parpadearía antes de que llegara — el clásico salto de «CavernicolApp» a logo
 * en cada clic. Por eso la promesa se guarda a nivel de módulo: la primera
 * pantalla la pide, las demás reciben lo ya resuelto.
 *
 * IMPORTANTE (bug 2026-07-30): la caché DEBE ir amarrada al tenant_id de la
 * sesión. Sin eso, al cambiar de negocio (login, logout, impersonación o
 * crear un tenant nuevo y entrar) el módulo seguía sirviendo el logo/nombre
 * del tenant ANTERIOR — caso real: Craft Arquitectos veía «Salon BEBE» en la
 * pestaña y el favicon. El servidor ya devolvía la marca correcta; el fallo
 * era solo de caché de pestaña.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';

let promesa = null;
let cache = null;
/** tenant_id con el que se resolvió `cache`. Null = sin marca cargada. */
let cacheTenantId = null;
// Quién está pintando con la marca ahora mismo. Sin esto, cambiar el color
// guardaba bien pero la barra lateral seguía rosa hasta recargar la pestaña: el
// panel y la barra son dos componentes distintos y nada le avisaba al segundo.
const atentos = new Set();

function _avisarATodos(m) {
  atentos.forEach((avisar) => {
    try { avisar(m); } catch { /* un oyente roto no rompe al resto */ }
  });
}

/**
 * Pide la marca del negocio de la sesión actual.
 *
 * @param {string|null|undefined} tenantId  Si se pasa y no coincide con la
 *   caché, se descarta lo viejo y se vuelve a pedir. Si no se pasa, se confía
 *   en el tenant_id que traiga la respuesta (caso «olvidar y re-leer»).
 */
export function pedirMarca(tenantId) {
  const tid = tenantId ? String(tenantId) : null;

  // Caché de OTRO tenant → basura. Tirarla antes de devolver nada.
  if (tid && cacheTenantId && cacheTenantId !== tid) {
    cache = null;
    promesa = null;
    cacheTenantId = null;
  }

  if (cache && (!tid || cacheTenantId === tid)) {
    return Promise.resolve(cache);
  }

  if (!promesa) {
    promesa = api.get('/tenant/branding')
      .then((r) => {
        cache = r.data;
        // Preferir el tenant_id de la respuesta (autoridad del servidor).
        cacheTenantId = (r.data && r.data.tenant_id)
          ? String(r.data.tenant_id)
          : tid;
        // Si pedimos para un tenant y el servidor dice otro, no confiar.
        if (tid && cacheTenantId && cacheTenantId !== tid) {
          cache = null;
          cacheTenantId = null;
          promesa = null;
          return null;
        }
        return cache;
      })
      .catch(() => {
        // Un fallo aquí NO debe romper la barra lateral: sin marca se cae al
        // nombre y color de casa, que es exactamente el comportamiento previo.
        promesa = null;
        cache = null;
        cacheTenantId = null;
        return null;
      });
  }
  return promesa;
}

/**
 * Olvida lo cacheado. Por defecto vuelve a pedirlo y avisa a quien pinta.
 *
 * `revalidar=false` sirve en logout / cambio de sesión: no hay que re-pedir
 * la marca del tenant que se está abandonando (y a veces ya no hay cookie).
 */
export function olvidarMarca({ revalidar = true } = {}) {
  cache = null;
  promesa = null;
  cacheTenantId = null;
  if (!revalidar) {
    _avisarATodos(null);
    return Promise.resolve(null);
  }
  return pedirMarca().then((m) => {
    _avisarATodos(m);
    return m;
  });
}

/**
 * La dirección del logo, con el sello del archivo pegado.
 *
 * El sello NO es decoración: la respuesta se sirve con caché de 24 horas, y sin
 * él la dirección del logo nuevo es idéntica a la del viejo — el navegador
 * entrega el que ya tenía guardado. Eso fue exactamente el bug de "cambio el
 * logo y me regresa el anterior". El sello lo da el servidor y cambia con cada
 * archivo, así que la caché larga pasa a ser correcta en vez de un estorbo.
 */
export function urlDeLogo(tenantId, variante, sello) {
  if (!tenantId) return null;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const url = `${base}/api/public/branding/${tenantId}/logo/${variante}`;
  return sello ? `${url}?v=${encodeURIComponent(sello)}` : url;
}

/**
 * @param {string|null|undefined} tenantId  Del usuario en sesión. Si cambia,
 *   se tira la caché y se pide de nuevo — es la guarda contra fugas entre
 *   negocios en la misma pestaña.
 */
export function useMarca(tenantId) {
  const tid = tenantId ? String(tenantId) : null;
  const [marca, setMarca] = useState(() => (
    cache && (!tid || cacheTenantId === tid) ? cache : null
  ));

  useEffect(() => {
    let vivo = true;
    const avisar = (m) => { if (vivo) setMarca(m); };
    atentos.add(avisar);

    if (!tid) {
      // Sin tenant (logout a medias, usuario platform): no pintar marca ajena.
      if (cacheTenantId) {
        cache = null;
        promesa = null;
        cacheTenantId = null;
      }
      setMarca(null);
    } else {
      pedirMarca(tid).then(avisar);
    }

    return () => { vivo = false; atentos.delete(avisar); };
  }, [tid]);

  return marca;
}
